import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Avatar, Box, InputLabel, Stack, TextField, Tooltip, Typography } from '@mui/material'
import {
  getGridBooleanOperators,
  getGridStringOperators,
  GridRenderCellParams,
  GridRowId,
  GridValueGetter,
} from '@mui/x-data-grid-premium'
import CircularStatusBar from 'components/DataGrid/components/CircularStatusBar'
import { customMonthRangeFilterOperators } from 'components/DataGrid/operators/customMonthRangeFilter'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import { customNumericSortOperator, customSortOperator } from 'components/DataGrid/operators/customSortOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import { customNotAnyOfOperator } from 'components/DataGrid/operators/isAnyOfOperator'
import { strictEqualsOperator } from 'components/DataGrid/operators/strictEqualsOperator'
import { NCO_STATUS_OPTIONS } from 'components/DataGrid/prototype/config'
import { selectEditCell } from 'components/DataGrid/prototype/editCells'
import { renderColorByString } from 'components/DataGrid/renderColorByString'
import RenderContractedByCell from 'components/Deals/grid/cells/RenderContractedByCell'
import AggregateCurrencyCell from 'components/MTB/grid/cells/AggregateCell'
import CommentCellHeader from 'components/MTB/grid/cells/CommentCellHeader'
import EditableDatePicker from 'components/NCO/EditableDatePicker'
import NextRoleCell from 'components/NCO/NextRoleCell'
import TeamMemberCell from 'components/Organizations/grid/cells/TeamMemberCell'
import RenderVertical from 'components/Organizations/RenderVertical'
import PandaDocViewer from 'components/PaymentSchedule/PandDocViewer'
import { formatDateString } from 'components/Tasks/taskUtil'
import SeoDisplayIcon from 'components/ui/seo-display-icon'
import dayjs from 'dayjs'
import MicrosoftSvg from 'images/icons/bing.png'
import GoogleSearchSvg from 'images/icons/Google Ads.png'
import GoogleDisplaySvg from 'images/icons/Google Display.png'
import GoogleSearchIcon from 'images/icons/google-search-icon.svg'
import MetaLightSvg from 'images/icons/meta_1.svg'
import Seo4Svg from 'images/icons/seo.png'
import Seo1Svg from 'images/icons/seotier1.svg'
import Seo2Svg from 'images/icons/seotier2.svg'
import Seo3Svg from 'images/icons/seotier3.svg'
import SimplifiSvg from 'images/icons/simplifi_logo.svg'
import SnapchatSvg from 'images/icons/snapchat.svg'
import SpotifySvg from 'images/icons/spotify.svg'
import TikTokSvg from 'images/icons/tik-tok.png'
import YoutubeSvg from 'images/icons/youtube.svg'
import { NcoStatusContainer } from 'styles/elements/styleElements'
import { StatusContainer } from 'styles/elements/tsElements'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import { User } from 'types'
import { formatDayjsModifiedTime } from 'utils/formatDayjsDateTime'
import { formatDate } from 'utils/formatters/formatDates'
import NcoCommentCell from './cells/NcoCommentCell'
import RenderNcoNameCell from './cells/RenderNcoNameCell'

export const capitalizeFirstLetter = string => {
  return string.charAt(0).toUpperCase() + string.slice(1)
}
export const displayChildRowProgress = row => {
  if (row?.productName === 'Linkedin Advertising') return row?.completedCounts?.linkedin * 100 || 0
  if (row?.productName === 'Meta Advertising') return row?.completedCounts?.fbpage * 100 || 0
  if (row?.productName === 'Programmatic Display Advertising') return null
  if (row?.productName === 'Spotify Advertising') return row?.completedCounts?.spotify * 100 || 0
  if (row?.productName === 'Microsoft Advertising') return row?.completedCounts?.microsoft * 100 || 0
  if (row?.productName === 'SnapChat Advertising') return null
  if (row?.productName === 'Youtube Advertising') return row?.completedCounts?.youtube * 100 || 0
  if (row?.productName === 'TikTok Advertising') return row?.completedCounts?.tiktok * 100 || 0
  if (row?.productName === 'Paid Search Advertising') return row?.completedCounts?.googleads * 100 || 0
  if (row?.productName === 'Google Display Advertising') return null
  if (row?.productName === 'Amazon Advertising') return null
  if (['SEO - Tier 1', 'SEO - Tier 2', 'SEO - Tier 3', 'SEO - Tier 4']?.includes(row?.productName))
    return row?.completedCounts?.completeseoform * 100 || 0
}
const boxStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  cursor: 'pointer',
}
const leftBoxStyles = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'left',
  alignItems: 'center',
  height: '100%',
}
export const renderNcoColumns = (
  density: 'compact' | 'standard' | 'comfortable',
  handleRowClick: (row: GridRenderCellParams['row']) => void,
  toggleExpanded: (id: GridRowId) => void,
  navigate?: (route: string) => void
) => {
  const densityStyles = getDensityStyles(density)
  const channelsArray = [
    {
      field: 'isGoogleDisplay',
      headerName: 'Google Display',
      src: GoogleDisplaySvg,
      type: 'srcIcon',
      width: 30,
    },
    {
      field: 'isGoogleSearch',
      headerName: 'Google Search',
      src: GoogleSearchSvg,
      type: 'srcIcon',
      width: 30,
    },
    {
      field: 'isMetaAdvertising',
      headerName: 'Meta Advertising',
      src: MetaLightSvg,
      type: 'srcIcon',
      width: 30,
    },
    {
      field: 'isMicrosoftAdvertising',
      headerName: 'Microsoft',
      src: MicrosoftSvg,
      type: 'srcIcon',
      width: 30,
    },
    { field: 'isSimplifi', headerName: 'Simplifi', src: SimplifiSvg, type: 'srcIcon', width: 30 },
    { field: 'isSnapchat', headerName: 'Snapchat', src: SnapchatSvg, type: 'srcIcon', width: 30 },
    { field: 'isSpotify', headerName: 'Spotify', src: SpotifySvg, type: 'srcIcon', width: 30 },
    { field: 'isTikTok', headerName: 'TikTok', src: TikTokSvg, type: 'srcIcon', width: 30 },
    { field: 'isYoutube', headerName: 'Youtube', src: YoutubeSvg, type: 'srcIcon', width: 30 },
    // { field: 'seoTier', headerName: 'SEO', src: GoogleSearchIcon, type: 'srcIcon', width: 30 },
    { field: 'isSeo1', headerName: 'SEO - Tier 1', tier: 1, src: Seo1Svg, type: 'seoIcon', width: 30 },
    { field: 'isSeo2', headerName: 'SEO - Tier 2', tier: 2, src: Seo2Svg, type: 'seoIcon', width: 30 },
    { field: 'isSeo3', headerName: 'SEO - Tier 3', tier: 3, src: Seo3Svg, type: 'seoIcon', width: 30 },
    { field: 'isSeo4', headerName: 'SEO - Tier 4', tier: 4, src: Seo4Svg, type: 'seoIcon', width: 30 },
  ]

  const renderChannels = () =>
    channelsArray?.map(channel => ({
      field: channel?.field,
      headerName: channel?.headerName,
      renderHeader: () => {
        return (
          <>
            {channel?.type === 'srcIcon' ? (
              <Tooltip title={channel?.headerName}>
                <img src={channel?.src} alt={channel?.headerName} style={{ height: 24 }} />
              </Tooltip>
            ) : (
              <SeoDisplayIcon title={channel?.headerName} tier={channel?.tier} />
            )}
          </>
        )
      },
      width: 150,
      headerAlign: 'center',
      disableColumnMenu: true,
      valueGetter: params => {
        return params ? 'Yes' : 'No'
      },
      renderCell: params => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            cursor: 'pointer',
            // bgcolor: renderColorByString(params.value),
          }}
        >
          {params?.value === 'Yes' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <CheckCircleIcon sx={{ height: '100%', color: renderColorByString(params.value) }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <CheckCircleIcon sx={{ height: '100%', color: 'rgb(96, 94, 94)', opacity: '0.2' }} />
            </Box>
          )}
        </Box>
      ),
    }))
  return [
    {
      field: 'name',
      aggregable: false,
      // headerName: 'Organization Name',
      headerClassName: 'org-name-header',
      renderHeader: () => {
        return (
          <Stack
            sx={{
              display: 'flex',
              flex: 1,
              '&.MuiDataGrid-columnHeaderTitleContainer': { flex: 1 },
              '&.MuiDataGrid-columnHeaderTitleContainerContent': { flex: 1 },
            }}
          >
            <Typography sx={{ fontSize: densityStyles.fontSize, flex: 1 }}>Organization Name</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'icon.website',
                  fontSize: densityStyles.labelSize,
                  textAlign: 'right',
                  flex: 1,
                }}
              >
                Hosting
              </Typography>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'icon.seo',
                  fontSize: densityStyles.labelSize,
                  textAlign: 'right',
                  flex: 1,
                }}
              >
                SEO
              </Typography>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  color: 'icon.nco',
                  fontSize: densityStyles.labelSize,
                  textAlign: 'right',
                  flex: 1,
                }}
              >
                Digital
              </Typography>
            </Box>
          </Stack>
        )
      },
      align: 'center',
      width: 350,
      valueFormatter: (_, row) => {
        const value = row?.isParent ? row?.organization?.name : `${row?.organization?.name}-${row?.name}`
        return value
      },
      valueGetter: (_, row) => row?.name,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <RenderNcoNameCell
            params={params}
            handleRowClick={handleRowClick}
            densityStyles={densityStyles}
            toggleExpanded={toggleExpanded}
          />
        )
      },
    },
    {
      field: 'ncoCommentCount',
      aggregable: false,
      pinnable: false,
      width: 40,
      // flex: 0.3,
      headerName: 'Comments',
      disableColumnMenu: true,
      renderHeader: () => <CommentCellHeader densityStyles={densityStyles} />,
      align: 'center',
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => {
        // Use ncoCommentCount from server-side index (NCO-specific comments)
        const count = row?.ncoCommentCount ?? 0
        return count
      },
      renderCell: (params: GridRenderCellParams) => {
        const { row } = params ?? {}
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null
        return (
          <NcoCommentCell params={params} densityStyles={densityStyles} route={`/nco/${row?.PK}/${row?.SK}/comments`} />
        )
      },
    },
    {
      field: 'total',
      headerName: 'Total',
      aggregable: true,
      type: 'number',
      width: 100,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => {
        if (row?.isAggregationRow) return row?.total ?? 0
        return parseFloat((row?.totals?.total || row?.contractTotal) ?? '0')
      },
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'isBypass',
      headerName: 'Bypass',
      type: 'boolean',
      hide: true,
      filterOperators: getGridBooleanOperators(),
    },

    {
      field: 'progress',
      headerName: 'Progress',
      width: 100,
      headerAlign: 'center',
      valueGetter: (_, row) => {
        const progress = row?.isParent ? row?.progress?.pct : !row?.isParent ? displayChildRowProgress(row) : 0
        return progress
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            {params?.row?.isParent ? (
              <CircularStatusBar
                progress={params?.value || 0}
                size={densityStyles.svgSize}
                fontSize={densityStyles.fontSize}
              />
            ) : !params?.row?.isParent && params?.value ? (
              <CircularStatusBar
                progress={params?.value || 0}
                size={densityStyles.svgSize}
                fontSize={densityStyles.fontSize}
              />
            ) : (
              <Box>–</Box>
            )}
          </Box>
        )
      },
    },
    {
      field: 'onboardingStatus',
      headerName: 'Onboarding Status',
      width: 125,
      headerAlign: 'center',
      editable: true,
      filterOperators: [
        ...getGridStringOperators(),
        strictEqualsOperator,
        customNotContainsOperator,
        customNotAnyOfOperator,
      ],
      valueGetter: (_, row) => {
        return row?.onboardingStatus ? row?.onboardingStatus : 'Pending'
      },
      valueSetter: (value, row) => {
        // For parent rows (deals), set onboardingStatus
        // For child rows, set status (though editing is disabled for children)
        // if (row?.isParent) {
        return { ...row, onboardingStatus: value }
        // }
        // return { ...row, status: value }
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return (
          <>
            {params?.row?.isParent ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  width: '100%',
                  cursor: 'pointer',
                  px: 1,
                  bgcolor: renderColorByString(params.value),
                }}
                onClick={handleClick}
              >
                <Typography
                  sx={{
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    textAlign: 'center',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: densityStyles.fontSize,
                  }}
                >
                  {params.value}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  px: 1,
                  color: renderColorByString(params?.value),
                }}
              >
                {params.value}
              </Box>
            )}
          </>
        )
      },
      // renderEditCell: params => <EditSelectCell params={params} options={ncoStatusFilters} />,
      renderEditCell: selectEditCell(NCO_STATUS_OPTIONS, false),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <StatusContainer type={params.value}>{params?.value}</StatusContainer>
          </Box>
        )
      },
    },
    {
      field: 'timeCompleted',
      headerName: 'Date Won',
      width: 100,
      valueGetter: (_, row) => {
        const value =
          row?.isParent && row?.timeCompleted
            ? formatDayjsModifiedTime(row?.timeCompleted)
            : !row?.isParent && row?.startDate
            ? formatDate(row?.startDate)
            : null
        return value ?? null
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }}
          >
            {params && params.value ? (
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'left',
                  width: '100%',
                  height: '100%',
                }}
              >
                {!params?.row?.isParent && (
                  <InputLabel sx={{ textAlign: 'left', fontSize: densityStyles.labelSize }}>Start Date</InputLabel>
                )}
                <Typography
                  sx={{
                    fontSize: densityStyles.fontSize,
                  }}
                >
                  {params.value}
                </Typography>
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: densityStyles.fontSize,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                {'—'}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'aging',
      headerName: 'Aging',
      width: 100,
      valueGetter: (_, row) => {
        const aging = dayjs().diff(dayjs(row.timeCompleted), 'day')
        return aging ?? 0
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            <Typography
              sx={{
                fontSize: densityStyles.fontSize,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              {params?.value > 0 ? params?.value : '—'}
            </Typography>
          </Box>
        )
      },
    },
    ...renderChannels(),
    {
      field: 'seoTier',
      headerName: 'SEO',
      renderHeader: () => {
        return (
          <Box>
            <img src={GoogleSearchIcon} alt="seoColumnIcon" style={{ height: 28, width: 28 }} />
          </Box>
        )
      },
      width: 75,
      headerAlign: 'center',
      valueGetter: params => {
        return params && params?.label ? params?.label : ''
      },
      renderCell: params => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            {params.value ? (
              <NcoStatusContainer sx={{ bgcolor: renderColorByString(params?.value), textAlign: 'center' }}>
                {params?.value}
              </NcoStatusContainer>
            ) : (
              <CheckCircleIcon sx={{ height: '100%', color: 'rgb(96, 94, 94)', opacity: '0.2' }} />
            )}
          </Box>
        )
      },
    },
    {
      field: 'dependenciesDueDate',
      headerName: 'Dependencies Due',
      width: 100,
      editable: true,
      valueGetter: (_, row) => {
        const value =
          row?.isParent && row?.dependenciesDueDate
            ? formatDateString(row?.dependenciesDueDate)
            : !row?.isParent && row?.endDate
            ? formatDate(row?.endDate)
            : null
        return value ?? null
      },
      valueSetter: (value, row) => {
        // For parent rows (deals), set dependenciesDueDate
        // For child rows (products), set endDate
        if (row?.isParent) {
          return { ...row, dependenciesDueDate: value }
        }
        return { ...row, endDate: value }
      },
      renderCell: params => {
        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }}
            onClick={handleClick}
          >
            {params && params.value ? (
              <Stack
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'left',
                  width: '100%',
                  height: '100%',
                }}
              >
                {!params?.row?.isParent && (
                  <InputLabel sx={{ textAlign: 'left', fontSize: densityStyles.labelSize }}>End Date</InputLabel>
                )}
                <Typography
                  sx={{
                    fontSize: densityStyles.fontSize,
                  }}
                >
                  {params.value}
                </Typography>
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: densityStyles.fontSize,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                {'—'}
              </Typography>
            )}
          </Box>
        )
      },
      renderEditCell: params => {
        const handleChange = async newValue => {
          await params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue ? newValue.toISOString() : null, // Save as ISO string
          })
          await params.api.stopCellEditMode({ id: params.id, field: params.field })
        }

        return <EditableDatePicker value={params.value} onChange={handleChange} />
      },
    },
    {
      field: 'slaDays',
      headerName: 'SLA Days',
      width: 75,
      headerAlign: 'center',
      editable: true,
      valueGetter: params => {
        return params || 0 // Default to 0 if no value is set
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
            onClick={handleClick}
          >
            {params.value ?? 'Set SLA Days'} {/* Display a default message if no value */}
          </Box>
        )
      },
      renderEditCell: params => {
        const handleChange = event => {
          const value = event.target.value ? parseInt(event.target.value, 10) : 0 // Parse the value as an integer
          params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: Math.min(100, Math.max(0, value)), // Clamp the value between 0 and 100
          })
        }

        return (
          <TextField
            type="number"
            value={params.value ?? 0} // Default value if not set
            onChange={handleChange}
            fullWidth
            autoFocus
            inputProps={{
              min: 0,
              max: 100,
              step: 1, // Control step increment
            }}
            sx={{
              textAlign: 'center', // Center the text in the input
            }}
          />
        )
      },
    },
    {
      field: 'estLaunchDate',
      headerName: 'Estimated Launch Date',
      width: 75,
      valueGetter: params => {
        const estLaunchDate = params
        return estLaunchDate ? dayjs(estLaunchDate).format('MM/DD/YYYY') : null
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        const estLaunchDate = params.value
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              cursor: 'pointer',
            }}
          >
            {estLaunchDate || (
              <Typography
                sx={{
                  fontSize: densityStyles.fontSize,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                {'—'}
              </Typography>
            )}
          </Box>
        )
      },
    },
    {
      field: 'dateLaunched',
      headerName: 'Date Launched',
      width: 100,
      editable: true,
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      filterOperators: customMonthRangeFilterOperators,
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        const dateLaunched = params?.row?.dateLaunched ? formatDateString(params?.row?.dateLaunched) : null

        const handleClick = () => {
          params.api.startCellEditMode({
            id: params.id,
            field: params.field,
          })
        }

        return (
          <>
            {params && params.value ? (
              <Box
                sx={{
                  ...boxStyles,
                }}
                onClick={handleClick}
              >
                {params.value}
              </Box>
            ) : dateLaunched ? (
              <Box
                sx={{
                  ...boxStyles,
                }}
                onClick={handleClick}
              >
                {dateLaunched}
              </Box>
            ) : (
              <Box
                sx={{
                  ...boxStyles,
                }}
                onClick={handleClick}
              >
                {'Set Date Launched'}
              </Box>
            )}
          </>
        )
      },
      renderEditCell: params => {
        const dateLaunched = params?.row?.dateLaunched ?? null
        const handleChange = async newValue => {
          await params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue ? newValue.toISOString() : null, // Save as ISO string
          })
          await params.api.stopCellEditMode({ id: params.id, field: params.field })
        }
        return <EditableDatePicker value={params.value || dateLaunched} onChange={handleChange} />
      },
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {params && params.value ? params.value : ''}
          </Box>
        )
      },
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params ? formatDateString(params) : null
      },
      renderCell: params => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {params && params.value ? params.value : ''}
          </Box>
        )
      },
    },
    {
      field: 'contractId',
      headerName: 'Contract',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        const previewUrl = `https://app.pandadoc.com/a/#/documents`
        const contractId = params && params.value ? params.value : null
        const contractUrl = previewUrl && contractId ? `${previewUrl}/${contractId}` : null
        const filename = params?.row?.name ?? 'No Filename'
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {contractUrl ? (
              <PandaDocViewer contractId={contractId} customFileName={filename} disabled={false} />
            ) : (
              <PandaDocViewer contractId={contractId} customFileName={filename} disabled={true} />
            )}
          </Box>
        )
      },
    },

    // {
    //   field: 'contractedBy',
    //   headerName: 'Contracted By',
    //   width: 100,
    //   headerAlign: 'left',
    //   filterOperators: [...getGridStringOperators(), customNotContainsOperator],
    //   valueGetter: params => {
    //     const contractedById = params && Array.isArray(params) ? params[0] : []
    //     const contractedBy = organizations ? organizations.find(org => org.id === contractedById) : null
    //     return contractedBy ? contractedBy.name : ''
    //   },
    //   renderCell: params => {
    //     const orgId =
    //       params?.row?.organizations && Array.isArray(params.row.organizations) && params.row.organizations[0]
    //         ? params.row.organizations[0]
    //         : null
    //     const org = organizations ? organizations.find(org => org.id === orgId) : null
    //     const contractedById = org && Array.isArray(org.contractedBy) ? org.contractedBy[0] : null

    //     return (
    //       <>
    //         {params && params.value ? (
    //           <RenderContractedByColumn
    //             key={`${contractedById}`}
    //             orgName={params.value ?? ''}
    //             orgId={contractedById}
    //             densityStyles={densityStyles}
    //           />
    //         ) : (
    //           <Box
    //             sx={{
    //               display: 'flex',
    //               flexDirection: 'row',
    //               justifyContent: 'left',
    //               alignItems: 'center',
    //               height: '100%',
    //               padding: densityStyles.padding,
    //               gap: densityStyles.gap,
    //             }}
    //           ></Box>
    //         )}
    //       </>
    //     )
    //   },
    //   sortComparator: (a, b) => {
    //     if (!a && !b) return 0 // Both are empty
    //     if (!a) return 1 // Treat empty as larger, so a (empty) goes after b (non-empty)
    //     if (!b) return -1 // Treat empty as larger, so b (empty) goes after a (non-empty)
    //     return a.localeCompare(b) // Standard string comparison for non-empty values
    //   },
    // },

    // {
    //   field: 'contractedByOrg',
    //   aggregable: false,
    //   pinnable: false,
    //   headerName: 'Contracted By',
    //   width: 100,
    //   // flex: 0.4,
    //   valueGetter: (_, row) => {
    //     return row?.contractedBy?.name ?? ''
    //   },
    //   renderCell: params => {
    //     return <ContractedByCell organizations={organizations} params={params} densityStyles={densityStyles} />
    //   },
    // },
    {
      field: 'contractedBy',
      headerName: 'Contracted By',
      aggregable: false,
      width: 100,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_value, row): GridValueGetter => {
        return row?.contractedBy?.name || ''
      },
      renderCell: params => {
        if (params?.rowNode?.isAutoGenerated || params?.row?.isAggregationRow) return null
        return <RenderContractedByCell params={params} densityStyles={densityStyles} navigate={navigate} />
      },
    },
    {
      field: 'status',
      headerName: 'Document Status',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'left',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            <StatusContainer type={params?.value || ''}>{capitalizeFirstLetter(params?.value || '')}</StatusContainer>
          </Box>
        )
      },
    },
    {
      field: 'id',
      headerName: 'Vertical',
      headerAlign: 'center',
      sortable: false,
      valueGetter: (_, row) => {
        const organization = row?.organization ?? null
        const verticals = organization && Array.isArray(organization.vertical) ? organization.vertical : []
        const verticalParams = verticals && Array.isArray(verticals) ? verticals[0] : []
        return verticalParams
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <RenderVertical
              vertical={params.value}
              width={densityStyles.verticalWidth}
              height={densityStyles.verticalHeight}
              size={densityStyles.verticalSize}
            />
          </Box>
        )
      },
    },

    {
      field: 'stage',
      headerName: 'Deal Stage',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        return params
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            <StatusContainer type={params?.value}>{params?.value}</StatusContainer>
          </Box>
        )
      },
    },
    {
      field: 'lastModified',
      headerName: 'Modified',
      width: 100,
      headerAlign: 'left',
      valueGetter: params => params,
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {formatDayjsModifiedTime(params && params.value ? params.value : 'No Modified Time')}
          </Box>
        )
      },
    },
    {
      field: 'products',
      headerName: 'SEO Price',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => {
        const seoProduct = params?.find(product => product.category === 'SEO')
        return seoProduct ? `$${seoProduct.price}` : 'N/A'
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              fontWeight: 'bold',
            }}
          >
            {params.value}
          </Box>
        )
      },
    },
    {
      field: 'owner',
      headerName: 'Owner',
      width: 65,
      headerAlign: 'center',
      valueGetter: (_, row) => {
        return row?.owner?.name ?? null
      },
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        const { rowNode } = params ?? {}
        const pinnedRows = rowNode?.type === 'pinnedRow'
        const avatarSize = pinnedRows ? densityStyles.avatarSizePinned : densityStyles.avatarSize
        const user = params?.row?.owner ?? null
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {user ? (
              <EntityAvatar name={user.name || ''} photoUrl={user.image_512} size="sm" showName={false} />
            ) : (
              <Box
                sx={{
                  width: avatarSize,
                  height: avatarSize,
                  borderRadius: '50%',
                  backgroundColor: 'lightgray',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: densityStyles.fontSize,
                }}
              >
                <Avatar sx={{ opacity: 0.1 }} />
              </Box>
            )}
          </Box>
        )
      },
    },
    {
      field: 'implementationFee',
      headerName: 'Impl. Fee',
      width: 100,
      headerAlign: 'center',
      valueGetter: params => params,
      renderCell: params => {
        if (params?.rowNode?.id === '__SERVER_AGGREGATIONS_ROW__') return null

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {params.value}
          </Box>
        )
      },
    },
    // {
    //   field: 'onboardingTeam',
    //   headerName: 'Team',
    //   minWidth: 100,
    //   headerAlign: 'center',
    //   valueGetter: (_, row) => {
    //     const usersArray = row?.onboardingTeam?.length > 0 ? row?.onboardingTeam : []
    //     return usersArray.map(user => user.name).join(', ') // Returns names as a string
    //   },
    //   renderCell: params => {
    //     const { rowNode } = params ?? {}
    //     const pinnedRows = rowNode?.type === 'pinnedRow'
    //     const avatarSize = pinnedRows ? densityStyles.avatarSizePinned : densityStyles.avatarSize
    //     const teamUsers = Array.isArray(params.row?.onboardingTeam) ? params.row.onboardingTeam : []
    //     return (
    //       <Box
    //         sx={{
    //           display: 'flex',
    //           justifyContent: 'center',
    //           alignItems: 'center',
    //           height: '100%',
    //           flexWrap: 'wrap',
    //           padding: densityStyles.padding,
    //           gap: densityStyles.gap,
    //         }}
    //         // onClick={() => handleClickEditStart(params)}
    //         // onClick={() => handleSelectUserRow(params?.rowNode?.id, role)}
    //       >
    //         <AvatarGroup
    //           max={densityStyles.avatarMax}
    //           spacing={densityStyles.avatarSpacing}
    //           slotProps={{
    //             additionalAvatar: { sx: { width: avatarSize, height: avatarSize } },
    //           }}
    //         >
    //           {teamUsers.length > 0 ? (
    //             teamUsers.map((user, index) => (
    //               <Tooltip key={`${user.user_uuid || user.id} - ${index}`} title={user?.role}>
    //                 <Avatar
    //                   key={`${user.user_uuid || user.id} - ${index}`}
    //                   src={user.image_512}
    //                   sx={{
    //                     width: avatarSize,
    //                     height: avatarSize,
    //                   }}
    //                 />
    //               </Tooltip>
    //             ))
    //           ) : (
    //             <>
    //               <Avatar sx={{ opacity: 0.1, width: avatarSize, height: avatarSize, bgcolor: 'background.overlay' }} />
    //               <Avatar sx={{ opacity: 0.1, width: avatarSize, height: avatarSize, bgcolor: 'background.overlay' }} />
    //               <Avatar sx={{ opacity: 0.1, width: avatarSize, height: avatarSize, bgcolor: 'background.overlay' }} />
    //             </>
    //           )}
    //         </AvatarGroup>
    //       </Box>
    //     )
    //   },
    // },
    {
      field: 'AM',
      headerName: 'AM',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['AM']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'BDE',
      headerName: 'BDE',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['BDE']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'DMS',
      headerName: 'DMS',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['DMS']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'MIA',
      headerName: 'MIA',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['MIA']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'PM',
      headerName: 'PM',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['PM']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'QA',
      headerName: 'QA',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['QA']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'SEO',
      headerName: 'SEO',
      width: 55,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['SEO']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'other',
      headerName: 'other',
      width: 85,
      valueGetter: (_, row) => {
        return row?._resolvedTeam?.['other']?.map((user: User) => user.name).join(', ')
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      renderCell: (params: GridRenderCellParams) => <TeamMemberCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'nextRole',
      width: 75,
      headerName: 'Active',
      headerAlign: 'center',
      valueGetter: (_, row) => {
        return row?.nextRole ?? null
      },
      renderCell: (params: GridRenderCellParams) => {
        return <NextRoleCell params={params} densityStyles={densityStyles} />
      },
    },

    // ...renderMarketingTeamColumns(
    //   roleArray,
    //   users,
    //   selectionModel,
    //   handleUserChange,
    //   densityStyles,
    //   handleClickEditStart,
    //   handleSelectUserRow
    // ),
  ]
}
export default renderNcoColumns
/////// renderDataGridColumns:
// 'organizations',
// 'onboardingStatus',
// 'type',
// 'timeCompleted',
// 'dependenciesDueDate',
// 'slaDays',
// 'estLaunchDate',
// 'dateLaunched',
// 'startDate',
// 'endDate',
// 'contractId',
// 'contractedBy',
// 'strategy',
// 'status',
// 'id',
// 'stage',
// 'lastModified',
// 'products',
// 'owner',
// 'implementationFee',
// 'total',

////// renderNcoColumns:
// 'organizations',
// 'onboardingStatus',
// 'type',
// 'timeCompleted',
// 'isGoogleDisplay',
// 'isGoogleSearch',
// 'isLinkedIn',
// 'isMetaAdvertising',
// 'isMicrosoftAdvertising',
// 'isSimplifi',
// 'isSnapchat',
// 'isSpotify',
// 'isTikTok',
// 'isYoutube',
// 'dependenciesDueDate',
// 'slaDays',
// 'estLaunchDate',
// 'dateLaunched',
// 'startDate',
// 'endDate',
// 'contractId',
// 'contractedBy',
// 'strategy',
// 'id',
// 'stage',
// 'lastModified',
// 'owner',
// 'implementationFee',
// 'total',
